/**
 * Fixed-point arithmetic for the authoritative simulation.
 *
 * All simulation positions, speeds and rates are integers scaled by FX_ONE.
 * Integers below 2^53 are exact in IEEE doubles, and Math.sqrt is required by
 * IEEE 754 to be correctly rounded, so every operation here produces identical
 * results across JavaScript engines (V8, JavaScriptCore).
 */
export const FX_SHIFT = 10;
export const FX_ONE = 1 << FX_SHIFT; // 1 tile = 1024 units
export const FX_HALF = FX_ONE >> 1;

export const toFx = (tiles: number): number => Math.round(tiles * FX_ONE);
export const fromFx = (v: number): number => v / FX_ONE;

/** Multiply two fixed-point values. */
export const fxMul = (a: number, b: number): number => Math.trunc((a * b) / FX_ONE);
/** Divide two fixed-point values. */
export const fxDiv = (a: number, b: number): number => Math.trunc((a * FX_ONE) / b);

/** Integer square root (floor), exact for inputs < 2^52. */
export function isqrt(n: number): number {
  if (n <= 0) return 0;
  let r = Math.floor(Math.sqrt(n));
  while (r * r > n) r--;
  while ((r + 1) * (r + 1) <= n) r++;
  return r;
}

/** Distance between two fixed-point points, in fixed-point units. */
export function fxDist(ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  return isqrt(dx * dx + dy * dy);
}

export function distSq(ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  return dx * dx + dy * dy;
}

/** Integer division rounding toward zero, guarded against division by zero. */
export const idiv = (a: number, b: number): number => (b === 0 ? 0 : Math.trunc(a / b));

export const clampInt = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/** Convert a per-second tile rate to fixed-point per tick. */
export const perTick = (tilesPerSecond: number, tickRate: number): number =>
  Math.round((tilesPerSecond * FX_ONE) / tickRate);

/**
 * Deterministic sine/cosine built only from IEEE basic arithmetic (which is
 * correctly rounded everywhere). Math.sin/cos are implementation-defined and
 * must never be used for authoritative state or map generation.
 */
export function detSin(x: number): number {
  const TWO_PI = 6.283185307179586;
  const PI = 3.141592653589793;
  x = x - TWO_PI * Math.floor(x / TWO_PI);
  if (x > PI) x -= TWO_PI;
  // Reduce to [-pi/2, pi/2]
  if (x > PI / 2) x = PI - x;
  else if (x < -PI / 2) x = -PI - x;
  const x2 = x * x;
  // Taylor series to x^13: error < 1e-10 on [-pi/2, pi/2]
  return x * (1 - (x2 / 6) * (1 - (x2 / 20) * (1 - (x2 / 42) * (1 - (x2 / 72) * (1 - (x2 / 110) * (1 - x2 / 156))))));
}
export const detCos = (x: number): number => detSin(x + 1.5707963267948966);
